import type { MessageKey } from '@dijitalapp/i18n';
import { INVITE_LINK_EXPIRY_HOURS, type ConversationDto, type GroupInviteLinkDto, type GroupJoinRequestDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChatStore } from '../lib/chat-context';

/** The shareable URL: the token rides in the fragment, which browsers never send to servers. */
export const joinUrl = (token: string) => `${location.origin}/join#${token}`;

/** Admin section of the group info dialog: invite link and pending join requests. */
export function GroupLinkAdmin({ conversation, onChanged }: { conversation: ConversationDto; onChanged: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const id = conversation.id;
  const pendingCount = conversation.group?.pendingRequests ?? 0;
  const [link, setLink] = useState<GroupInviteLinkDto | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [requireApproval, setRequireApproval] = useState(false);
  const [expiry, setExpiry] = useState('never');
  const [copied, setCopied] = useState(false);
  const [requests, setRequests] = useState<GroupJoinRequestDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<{ link: GroupInviteLinkDto | null }>('GET', `/groups/${id}/invite-link`).then((r) => setLink(r.link), () => undefined);
  }, [id]);
  useEffect(() => {
    void api<{ requests: GroupJoinRequestDto[] }>('GET', `/groups/${id}/join-requests`).then((r) => setRequests(r.requests), () => undefined);
  }, [id, pendingCount]);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const create = () =>
    run(async () => {
      const r = await api<{ token: string; link: GroupInviteLinkDto }>('POST', `/groups/${id}/invite-link`, {
        requireApproval,
        expiresInHours: expiry === 'never' ? null : Number(expiry),
      });
      setLink(r.link);
      setToken(r.token);
      setCopied(false);
    });

  const revoke = () =>
    run(async () => {
      await api('DELETE', `/groups/${id}/invite-link`);
      setLink(null);
      setToken(null);
    });

  const decide = (userId: string, approve: boolean) =>
    run(async () => {
      await api('POST', `/groups/${id}/join-requests/${userId}`, { approve });
      setRequests((list) => list.filter((r) => r.id !== userId));
      if (approve) onChanged();
      await store?.refreshConversation(id);
    });

  const copy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(joinUrl(token));
      setCopied(true);
    } catch {
      /* the field stays selectable for manual copy */
    }
  };

  return (
    <>
      <h3>{t('group.inviteLink')}</h3>
      <p className="hint">{t('group.linkHint')}</p>
      {link ? (
        <p className="muted">
          {t('group.linkActive', { date: i18n.formatDate(link.createdAt), uses: link.useCount })}
          {link.expiresAt && ` · ${t('group.linkExpires', { date: i18n.formatDate(link.expiresAt) })}`}
          {link.requireApproval && ` · ${t('group.requireApproval')}`}
        </p>
      ) : (
        <p className="muted">{t('group.noLink')}</p>
      )}
      {token && (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <label className="visually-hidden" htmlFor="gl-url">
              {t('group.inviteLink')}
            </label>
            <input id="gl-url" className="input" readOnly value={joinUrl(token)} onFocus={(e) => e.currentTarget.select()} />
            <button className="btn" type="button" onClick={() => void copy()}>
              {copied ? t('group.linkCopied') : t('group.copyLink')}
            </button>
          </div>
          <p className="hint">{t('group.linkOnce')}</p>
        </div>
      )}
      <label className="check">
        <input type="checkbox" checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />
        <span>{t('group.requireApproval')}</span>
      </label>
      <div className="row">
        <label htmlFor="gl-expiry">{t('group.linkExpiry')}</label>
        <select id="gl-expiry" className="input" style={{ width: 'auto' }} value={expiry} onChange={(e) => setExpiry(e.target.value)}>
          <option value="never">{t('group.expiry.never')}</option>
          {INVITE_LINK_EXPIRY_HOURS.map((h) => (
            <option key={h} value={String(h)}>
              {t(`group.expiry.${h}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <button className="btn btn-primary" type="button" onClick={() => void create()}>
          {link ? t('group.resetLink') : t('group.createLink')}
        </button>
        {link && (
          <button className="btn btn-danger" type="button" onClick={() => void revoke()}>
            {t('group.revokeLink')}
          </button>
        )}
      </div>

      <h3>{t('group.joinRequests')}</h3>
      {requests.length === 0 ? (
        <p className="muted">{t('group.noRequests')}</p>
      ) : (
        <div className="stack" style={{ gap: 0 }}>
          {requests.map((r) => (
            <div key={r.id} className="toggle-row">
              <div style={{ minWidth: 0 }}>
                <strong>{r.displayName}</strong> <span className="muted">@{r.username}</span>{' '}
                {r.previouslyRemoved && <span className="badge">{t('group.previouslyRemoved')}</span>}
              </div>
              <span className="spacer" />
              <button className="link-button" type="button" onClick={() => void decide(r.id, true)}>
                {t('group.approve')}
              </button>
              <button className="link-button" type="button" onClick={() => void decide(r.id, false)}>
                {t('group.reject')}
              </button>
            </div>
          ))}
        </div>
      )}
      {error && (
        <p role="alert" className="alert alert-error">
          {error}
        </p>
      )}
    </>
  );
}
